import { AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/Domain/components/Shared/AlertDialog/AlertDialog';
import { Button } from '@/Domain/components/Shared/Button/Button';
import { Separator } from '@/Domain/components/Shared/Separator/Separator';
import { Spinner } from '@/Domain/components/Shared/Spinner';

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  isLoading: boolean;
};

export const DeleteDatasetDialog = ({ open, onOpenChange, onConfirm, isLoading }: Props) => {
  const { t } = useTranslation('simulation_page');

  const handleRequestClose = () => {
    if (isLoading) {
      return;
    }
    onOpenChange(false);
  };

  return (
    <AlertDialog open={open} onOpenChange={(value) => !value && handleRequestClose()}>
      <AlertDialogContent onOverlayClick={handleRequestClose}>
        <AlertDialogHeader>
          <AlertDialogTitle>
            <AlertTriangle className="rounded-full bg-destructive-muted p-1.5 text-destructive box-content h-5 w-5" />
            {t('delete_confirmation.title')}
          </AlertDialogTitle>
          <AlertDialogDescription>{t('delete_confirmation.description')}</AlertDialogDescription>
        </AlertDialogHeader>
        <Separator />
        <AlertDialogFooter>
          <AlertDialogCancel asChild disabled={isLoading}>
            <Button
              data-testid="delete-dialog-cancel"
              variant="secondary"
              size="sm"
              disabled={isLoading}
            >
              {t('delete_confirmation.cancel')}
            </Button>
          </AlertDialogCancel>
          <Button
            data-testid="delete-dialog-confirm"
            variant="danger"
            size="sm"
            disabled={isLoading}
            onClick={onConfirm}
          >
            {isLoading && <Spinner className="mr-1.5 h-4 w-4" />}
            {t('delete_confirmation.confirm')}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
